// Slack mrkdwn parser. Returns a flat-ish AST of MrkdwnNode segments
// the renderer can iterate.
//
// What we handle (Pass 1.4):
//   *bold*           single asterisk
//   _italic_         single underscore
//   ~strike~         single tilde
//   `inline code`    single backtick
//   ```block```      triple backtick (multi-line)
//   >blockquote      `>` at line start (single-line; consecutive lines
//                    merge into one quote block)
//
// What we deliberately do NOT handle:
//   - Standard markdown lists (Slack's mrkdwn doesn't have them; the
//     literal `•` / `-` characters are just text).
//   - Workspace custom emoji (handled by the renderer; we only deal
//     with structure).
//   - Links of the form [text](url) — Slack uses <url|text>, parsed
//     elsewhere by the escape-syntax pass in slack-text.tsx.
//
// Boundary rules for inline formatters (Slack-faithful, not generic
// markdown): the opening delimiter must be at start-of-string OR
// preceded by a non-word character; the closing delimiter must be at
// end-of-string OR followed by a non-word character. This is what
// keeps `snake_case_variable` from rendering as italics.
//
// Code spans (single AND triple-backtick) suppress all other
// formatting inside; their text is preserved verbatim.

export type MrkdwnNode =
  | { kind: "text"; text: string }
  | { kind: "bold"; children: MrkdwnNode[] }
  | { kind: "italic"; children: MrkdwnNode[] }
  | { kind: "strike"; children: MrkdwnNode[] }
  | { kind: "inline_code"; text: string }
  | { kind: "code_block"; text: string }
  | { kind: "blockquote"; children: MrkdwnNode[] };

const MAX_DEPTH = 4;

// Top-level: extract triple-backtick code blocks first (they span
// multiple lines and suppress everything inside).
export function parseMrkdwn(text: string): MrkdwnNode[] {
  if (!text) return [];
  const out: MrkdwnNode[] = [];
  const re = /```([\s\S]+?)```/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      out.push(...parseBlocks(text.slice(lastIdx, m.index)));
    }
    out.push({ kind: "code_block", text: m[1]! });
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) {
    out.push(...parseBlocks(text.slice(lastIdx)));
  }
  return out;
}

// Block level: blockquotes (line-start `>`). Consecutive `>` lines
// merge into a single blockquote.
function parseBlocks(text: string): MrkdwnNode[] {
  const out: MrkdwnNode[] = [];
  const lines = text.split("\n");
  let textBuf: string[] = [];
  let quoteBuf: string[] | null = null;

  const flushText = () => {
    if (textBuf.length === 0) return;
    out.push(...parseInline(textBuf.join("\n")));
    textBuf = [];
  };
  const flushQuote = () => {
    if (!quoteBuf) return;
    out.push({ kind: "blockquote", children: parseInline(quoteBuf.join("\n")) });
    quoteBuf = null;
  };

  for (const line of lines) {
    if (/^>/.test(line)) {
      flushText();
      if (!quoteBuf) quoteBuf = [];
      quoteBuf.push(line.replace(/^>\s?/, ""));
    } else {
      flushQuote();
      textBuf.push(line);
    }
  }
  flushText();
  flushQuote();
  return out;
}

// Inline level: extract single-backtick inline code (which suppresses
// all further formatting inside), then parse remaining segments for
// bold / italic / strike with boundary rules.
function parseInline(text: string): MrkdwnNode[] {
  if (text.length === 0) return [];
  const out: MrkdwnNode[] = [];
  const re = /`([^`\n]+)`/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      out.push(...parseFormatting(text.slice(lastIdx, m.index), 0));
    }
    out.push({ kind: "inline_code", text: m[1]! });
    lastIdx = re.lastIndex;
  }
  if (lastIdx < text.length) {
    out.push(...parseFormatting(text.slice(lastIdx), 0));
  }
  return out;
}

interface FormatterMatch {
  kind: "bold" | "italic" | "strike";
  // Index of the opening delimiter character in the input text.
  delimIdx: number;
  // The captured content between opening and closing delimiters.
  content: string;
}

// Find the earliest formatter match in `text` that satisfies Slack's
// boundary rules. Returns null if none found.
function findFormatter(text: string): FormatterMatch | null {
  // Each pattern's outer non-capturing group is the boundary char or
  // start-of-string; group 1 is the content.
  const patterns: Array<{ kind: FormatterMatch["kind"]; re: RegExp; delim: string }> = [
    { kind: "bold", re: /(^|[^\w*])\*([^*\n]+?)\*(?=$|[^\w*])/g, delim: "*" },
    { kind: "italic", re: /(^|[^\w_])_([^_\n]+?)_(?=$|[^\w_])/g, delim: "_" },
    { kind: "strike", re: /(^|[^\w~])~([^~\n]+?)~(?=$|[^\w~])/g, delim: "~" },
  ];

  let best: FormatterMatch | null = null;
  for (const p of patterns) {
    p.re.lastIndex = 0;
    const m = p.re.exec(text);
    if (m) {
      const before = m[1] ?? "";
      const delimIdx = m.index + before.length;
      if (!best || delimIdx < best.delimIdx) {
        best = { kind: p.kind, delimIdx, content: m[2]! };
      }
    }
  }
  return best;
}

function parseFormatting(text: string, depth: number): MrkdwnNode[] {
  if (text.length === 0) return [];
  if (depth >= MAX_DEPTH) {
    return [{ kind: "text", text }];
  }

  const found = findFormatter(text);
  if (!found) {
    return [{ kind: "text", text }];
  }

  const out: MrkdwnNode[] = [];
  const beforeText = text.slice(0, found.delimIdx);
  if (beforeText.length > 0) {
    out.push({ kind: "text", text: beforeText });
  }
  out.push({
    kind: found.kind,
    children: parseFormatting(found.content, depth + 1),
  });
  // 1 (opening delim) + content.length + 1 (closing delim)
  const afterStart = found.delimIdx + 1 + found.content.length + 1;
  const afterText = text.slice(afterStart);
  if (afterText.length > 0) {
    out.push(...parseFormatting(afterText, depth));
  }
  return out;
}
